import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { publicApi } from "../lib/api";
import { ArrowUpRight, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get("/blog")
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="blog-page" className="relative min-h-screen">
      <Header />
      <section className="pt-40 md:pt-48 pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand-muted mb-6 flex items-center gap-3">
            <span className="h-px w-8 bg-brand-blue" />
            The Journal — Painting Tips
          </div>
          <h1 className="font-display uppercase text-6xl md:text-8xl leading-[0.85] tracking-tight mb-6">
            Painting <span className="text-brand-blue">Tips</span> <br />
            &amp; Field Notes.
          </h1>
          <p className="text-brand-muted text-base md:text-lg max-w-2xl">
            Straight from the crew — how to prep, what paint to buy, and the
            little details that separate a decent job from a great one.
          </p>
        </div>
      </section>

      <section className="pb-32 md:pb-48">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex items-center gap-2 text-brand-muted">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading posts…
            </div>
          ) : posts.length === 0 ? (
            <div data-testid="blog-empty" className="border border-brand-border bg-brand-surface p-12 max-w-xl">
              <h2 className="font-display uppercase text-3xl mb-2">
                First article coming soon.
              </h2>
              <p className="text-brand-muted">
                Follow us for painting tips, prep guides, and behind-the-scenes
                from the crew.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  data-testid={`blog-card-${p.slug}`}
                >
                  <Link to={`/blog/${p.slug}`} className="group block border border-brand-border bg-brand-surface hover:border-brand-blue transition-colors">
                    {p.cover_image && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col gap-3">
                      <div className="text-[10px] tracking-[0.28em] uppercase text-brand-muted">
                        {formatDate(p.created_at)}{p.tags?.length ? ` · ${p.tags[0]}` : ""}
                      </div>
                      <h2 className="font-display uppercase text-2xl md:text-3xl leading-[0.95]">
                        {p.title}
                      </h2>
                      {p.excerpt && (
                        <p className="text-brand-muted text-sm line-clamp-3">{p.excerpt}</p>
                      )}
                      <span className="mt-2 inline-flex items-center gap-2 text-brand-blue text-xs uppercase tracking-widest">
                        Read <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
